<template>
  <div class="nav no-print" ref="nav" :class="{'extra-padding': isIphoneX}">
      <div v-if="preview" class="preview gtm-edit-wb" @click="edit"> <span class="preview-text"> <span class="is-hidden-mobile">{{ $t('magicboard.previewMode')}}:</span> {{ $t('magicboard.clickHereTo') }} </span> <span class="icon gtm-edit-wb" aria-label="Edit Whiteboard"><i class="fal fa-pencil-alt gtm-edit-wb" aria-hidden="true"></i></span> {{ $t('magicboard.editWhiteboard') }}</div>
      <div class="menus is-flex">
        <!-- Left tools -->
        <ul class="menu-list menu-1 is-flex">
          <li v-for="(section, sectionOffset) in menu.sections.left" :class="sectionItemContainerClass(section, sectionOffset)" class="tool-item-container" :key="sectionOffset">
            <div :ref="section.name" :class="sectionContainerClass('left', section, sectionOffset)" @click="clickSection('left', section, sectionOffset)" class="tool-item" :data-balloon="section.tooltip" data-balloon-pos="up">
              <a :class="sectionClass('left', section, sectionOffset)" :data-name="section.name" class="tool-icon" :aria-label="section.name"></a>
            </div>
          </li>
        </ul>
        <!-- Center tools -->
        <ul class="menu-list menu-2 is-flex">
          <li v-for="(section, sectionOffset) in menu.sections.centre" :class="sectionItemContainerClass(section, sectionOffset)" class="tool-item-container" :key="sectionOffset">
            <div :ref="section.name" :class="sectionContainerClass('centre', section, sectionOffset)" @click="clickSection('centre', section, sectionOffset)" class="tool-item" :data-balloon="section.tooltip" data-balloon-pos="up">
              <a :class="sectionClass('centre', section, sectionOffset)" :data-name="section.name" class="tool-icon" :aria-label="section.name"></a>
            </div>
            <ul v-if="ifShowSectionItems('centre', section, sectionOffset)" class="submenu is-flex">
              <template v-for="(item, itemOffset) in section.items">
                <li v-if="(!item.debugOnly || debug) && (isAdmin || !item.adminOnly)"  class="sub-item-container" :key="itemOffset">
                  <div :data-balloon="item.tooltip" data-balloon-pos="up" @click="clickItem(section, item, sectionOffset, itemOffset)" class="sub-item is-hidden-mobile">
                    <a :class="itemClass('centre', section, item, sectionOffset, itemOffset)" class="sub-icon" :aria-label="item.name"></a>
                  </div>
                  <div @click="clickItem(section, item, sectionOffset, itemOffset)" class="sub-item is-hidden-tablet" :data-balloon="item.tooltip" data-balloon-pos="up">
                    <a :class="itemClass('centre', section, item, sectionOffset, itemOffset)" class="sub-icon" :aria-label="item.name"></a>
                  </div>
                </li>
              </template>
            </ul>
          </li>
        </ul>
      </div>
  </div>
</template>

<script>
import auth from '@/libs/auth'
import userAgent from '@/libs/user-agent'
import i18n from '@/i18n'

import componentState from '@/mixins/component-state'

const hideMobile = -1
const showMobile = 0
const onlyMobile = 1

const debug = process.env.NODE_ENV !== 'production'

const menu = {
  sections: {
    left: [
      {
        name: 'Pan',
        icon: 'fal fa-hand-paper',
        mobile: showMobile,
        changesMode: true,
        tooltip: i18n.t('magicboard.pan')
      },
      {
        name: 'Select',
        icon: 'fal fa-mouse-pointer',
        mobile: showMobile,
        changesMode: true,
        tooltip: i18n.t('magicboard.select')
      }
    ],

    centre: [
      {
        name: 'Shape',
        icon: 'icon-shapes',
        items: [
          { name: 'Line', icon: 'icon-line', tooltip: i18n.t('magicboard.line') },
          { name: 'Arrow', icon: 'fal fa-long-arrow-right', tooltip: i18n.t('magicboard.arrow') },
          { name: 'Rectangle', icon: 'icon-rectangle', tooltip: i18n.t('magicboard.rectangle') },
          { name: 'Ellipse', icon: 'icon-ellipse', tooltip: i18n.t('magicboard.ellipse') },
          { name: 'Triangle', icon: 'icon-triangle', tooltip: i18n.t('magicboard.triangle') }
        ],
        mobile: hideMobile,
        changesMode: true,
        tooltip: i18n.t('magicboard.shape')
      },
      {
        name: 'Text',
        icon: 'icon-textbox',
        mobile: hideMobile,
        changesMode: true,
        tooltip: i18n.t('magicboard.text')
      },
      {
        name: 'Image',
        icon: 'fal fa-camera',
        mobile: hideMobile,
        changesMode: true,
        tooltip: i18n.t('magicboard.image')
      },
      {
        name: 'Pencil',
        icon: 'fal fa-pencil-alt',
        mobile: hideMobile,
        changesMode: true,
        tooltip: i18n.t('magicboard.pencil')
      },
      {
        name: 'Sticky Note',
        icon: 'fal fa-sticky-note',
        mobile: hideMobile,
        changesMode: true,
        showName: 'Note',
        tooltip: i18n.t('magicboard.sticky')
      },
      {
        name: 'Add',
        icon: 'fal fa-plus',
        items: [
          { name: 'Line', icon: 'icon-line', tooltip: i18n.t('magicboard.line') },
          { name: 'Arrow', icon: 'fal fa-long-arrow-right', tooltip: i18n.t('magicboard.arrow') },
          { name: 'Rectangle', icon: 'icon-rectangle', tooltip: i18n.t('magicboard.rectangle') },
          { name: 'Ellipse', icon: 'icon-ellipse', tooltip: i18n.t('magicboard.ellipse') },
          { name: 'Triangle', icon: 'icon-triangle', tooltip: i18n.t('magicboard.triangle') },
          { name: 'Pencil', icon: 'fal fa-pencil-alt', tooltip: i18n.t('magicboard.pencil') },
          { name: 'Text', icon: 'icon-textbox', tooltip: i18n.t('magicboard.text') },
          { name: 'Image', icon: 'fal fa-camera', tooltip: i18n.t('magicboard.image') },
          { name: 'Sticky Note', icon: 'fal fa-sticky-note', tooltip: i18n.t('magicboard.sticky') }
        ],
        mobile: onlyMobile,
        changesMode: true,
        tooltip: i18n.t('magicboard.add')
      },
      {
        name: 'Erase',
        icon: 'fal fa-eraser',
        changesMode: true,
        tooltip: i18n.t('magicboard.erase')
      }
    ]
  }
}

export default {
  mixins: [componentState],
  props: ['callback', 'canvasSize', 'preview', 'edit'],
  data: function () {
    return {
      selectedSectionName: '',
      selectedSection: [],
      selectedItem: [],
      showItems: [],
      state: {},
      refresh: false,
      menu,
      debug,
      windowSize: 0
    }
  },
  computed: {
    isAdmin: auth.isAdmin,
    isApp: userAgent.isApp,
    isIphoneX: userAgent.isIphoneX,
    isIOSChrome: userAgent.isIOSChrome
  },
  mounted () {
    this.windowSize = window.innerWidth
  },
  methods: {
    sectionGroup: function (section) {
      return section.changesMode ? 0 : 1
    },

    isSectionActive: function (name, section, offset) {
      if (this.selectedSectionName !== name) {
        return false
      }

      const group = this.sectionGroup(section)
      return offset === this.selectedSection[group]
    },

    sectionItemContainerClass: function (section, offset) {
      return {
        'is-hidden-mobile': section.mobile === hideMobile,
        'is-hidden-tablet': section.mobile === onlyMobile
      }
    },

    sectionContainerClass: function (name, section, offset) {
      const cls = {}
      cls[section.name] = true
      return cls
    },

    sectionClass: function (name, section, offset) {
      const cls = {
        'is-active': this.isSectionActive(name, section, offset),
        'is-hidden-mobile': section.mobile === hideMobile,
        'is-hidden-desktop': section.mobile === onlyMobile,
        refresh: this.refresh // work-around for active flag not working
      }
      cls[section.icon] = true
      return cls
    },

    clickSection: function (sectionName, section, offset) {
      this.selectedSectionName = sectionName

      const group = this.sectionGroup(section)
      if (this.selectedSection[group] !== offset) {
        this.selectedItem[group] = -1
      }

      let alreadyOpen = false
      if (this.selectedSection[group] === offset) {
        alreadyOpen = this.showItems[group] === true
      }

      // close other open section
      if (!alreadyOpen && this.showItems !== []) {
        this.cancel()
      }

      this.selectedSection[group] = offset
      this.showItems = []
      this.refresh = !this.refresh

      this.$refs[section.name][0].blur()

      if (section.items) {
        this.showItems[group] = !alreadyOpen
      }

      if (!section.items) {
        this.saveState(offset)
        this.callback(section.name)
        if (!section.changesMode) {
          this.clear(group)
        }
      } if (alreadyOpen) {
        this.cancel()
      }
    },

    ifShowSectionItems: function (name, section, sectionOffset) {
      const group = this.sectionGroup(section)
      return this.isSectionActive(name, section, sectionOffset) && this.showItems[group] && !!section.items
    },

    isItemActive: function (name, section, sectionOffset, itemOffset) {
      const group = this.sectionGroup(section)
      return this.isSectionActive(name, section, sectionOffset) && this.selectedItem[group] === itemOffset
    },

    itemClass: function (name, section, item, sectionOffset, itemOffset) {
      const cls = { 'is-active': this.isItemActive(name, section, sectionOffset, itemOffset) }
      cls[item.icon] = true
      return cls
    },

    clickItem: function (section, item, sectionOffset, itemOffset) {
      const group = this.sectionGroup(section)
      this.selectedItem[group] = itemOffset
      this.showItems = []
      this.saveState(sectionOffset, itemOffset)
      this.callback(item.name)
      if (!section.changesMode) {
        this.clear(group)
      }
    },

    clear: function (group) {
      this.showItems = []
      if (group) {
        this.selectedSection[group] = -1
        this.selectedItem[group] = -1
      } else {
        this.selectedSection[1] = -1
        this.selectedItem[1] = -1
      }
    },

    cancel: function () {
      this.clear()
      if (this.state.currentSection >= 0) {
        const section = this.menu.sections.centre[this.state.currentSection]
        if (section && section.changesMode) {
          const group = this.sectionGroup(section)
          this.selectedSection[group] = this.state.currentSection
          this.selectedItem[group] = this.state.currentItem
        }
      }
    },

    revert: function () {
      this.state.currentSection = this.state.previousSection >= 0 ? this.state.previousSection : -1
      this.state.currentItem = this.state.previousItem >= 0 ? this.state.previousItem : -1
      this.state.previousSection = -1
      this.state.previousItem = -1
      this.cancel()
    },

    select: function (name) {
      Object.keys(this.menu.sections).forEach((sectionName) => {
        this.menu.sections[sectionName].forEach((section, offset) => {
          if (section.name === name) {
            return this.clickSection(sectionName, section, offset)
          }
        })
      })
    },

    saveState: function (sectionOffset, itemOffset) {
      this.state.previousSection = this.state.currentSection >= 0 ? this.state.currentSection : -1
      this.state.previousItem = this.state.currentItem >= 0 ? this.state.currentItem : -1
      this.state.currentSection = sectionOffset >= 0 ? sectionOffset : -1
      this.state.currentItem = itemOffset >= 0 ? itemOffset : -1
    }
  }
}
</script>

<style lang="scss" scoped>
  @import '~styles/utils.scss';

  @font-face {
      font-family: 'Glyphter';
      src: url('/static/fonts/mb-font/Glyphter.eot');
      src: url('/static/fonts/mb-font/Glyphter.eot?#iefix') format('embedded-opentype'),
          url('/static/fonts/mb-font/Glyphter.woff') format('woff'),
          url('/static/fonts/mb-font/Glyphter.ttf') format('truetype'),
          url('/static/fonts/mb-font/Glyphter.svg#Glyphter') format('svg');
      font-weight: normal;
      font-style: normal;
  }

  [class*='icon-']:before{
    display: inline-block;
    font-family: 'Glyphter';
    font-style: normal;
    font-weight: normal;
    font-size: 18px;
    -webkit-font-smoothing: antialiased;
    -moz-osx-font-smoothing: grayscale
  }

  .icon-shapes:before{content:'\0041'; font-weight: bold;}
  .icon-line:before{content:'\0042'; font-weight: bold;}
  .icon-rectangle:before{content:'\0043';}
  .icon-ellipse:before{content:'\0044';}
  .icon-triangle:before{content:'\0045';}
  .icon-textbox:before{content:'\0046'; font-weight: bold;}
  .icon-bringtofront:before{content:'\0047';}
  .icon-sendtoback:before{content:'\0048';}
  .icon-grid:before{content:'\0049'; font-weight: bold;}
  .icon-export:before{content:'\004a';}

  .nav {
    height: 4rem;
    width: 100%;
    background-color: transparent;
    z-index: 3;
    position: relative;
    text-align: center;
    @include touch {
      width: 100%;
      margin-right: 0;
    }
    &.extra-padding {
      margin-bottom: 50px;
    }
  }

  .disabled {
    pointer-events: none;
    opacity: 0.25;
  }

  .menu-list {
    justify-content: center;
    height: 55px;
    width: 100%;
    margin: auto;
    .tool-item-container {
      display: block;
      float: left;
      height: 100%;
      .tool-item {
        border-radius: 50%;
        height: 100%;
        width: 55px;
        background-color: #F0F0F0;
        transition:0.1s;
        margin: 0 0.5rem;
        @include touch {
          margin: 0 0.25rem;
        }
      }
      .tool-icon {
        display: flex;
        justify-content: center;
        align-items: center;
        height: 100%;
        border-radius: 50%;
        &:hover,
        &:focus {
          color: $primary;
        }
        &.is-active {
          background: $primary;
          color: white;
        }
      }
    }
  }

  .icon-shapes:before, .icon-textbox:before{
    font-size: 0.9rem;
    line-height: 0.6;
  }
  .preview {
    position: absolute;
    margin: 0 auto;
    border-radius: 0px;
    z-index: 4;
    background: #CCC;
    top: 0;
    bottom: 0;
    left: 0;
    right: 0;
    opacity: .9;
    line-height: 2.5rem;
    font-weight: bold;
    cursor: pointer;
    display: flex;
    justify-content: center;
    align-items: center;
  }

  .preview-text {
    color: $primary;
  }

  .submenu {
    border-left: none;
    position: absolute;
    bottom: 120%;
    width: 80vw;
    justify-content: center;
    left: 0;
    margin: 0 auto;
    @include mobile {
      width: 100%;
      flex-wrap: wrap;
      padding: 0 10%;
    }
    .sub-item-container {
      display: block;
      margin: 0 0.5rem;
      @include mobile {
        margin: 0.5rem;
      }
      .sub-item {
        height: 65px;
        width: 65px;
      }
      .sub-icon {
        @include shadow;
        background-color: whitesmoke;
        height: 100%;
        position: relative;
        display: flex;
        justify-content: center;
        align-items: center;
        font-size: 1.0rem;
        border-radius: 50%;
        &:hover,
        &:focus {
          color: $primary;
        }
        &.is-active {
          background: $primary;
          color: white;
        }
      }
    }
  }

  .menu-1 {
    width: 25%;
    @include touch {
      width: inherit;
    }
    @include mobile{
      // width: 50%;
    }
  }
  .menu-2 {
    width: 75%;
    @include touch {
      // flex-grow: 2
      width: inherit;
    }
  }
</style>
